import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppButton,
  Box,
  Dropdown,
  Option,
  OptionKey,
  Preloader,
  RawButton,
  SearchInput,
  Text,
  useDebounceState,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { useCurrentOrganization } from '@/modules/core/hooks';

import { useRolePartnersQuery } from '../../queries';
import { RoleLevel } from '../../role.types';

import RolesLevelPartnerDropdown from './RolesLevelPartnerDropdown';
import RolesLevelRadio from './RolesLevelRadio';

interface Props {
  level: RoleLevel;
  onSetLevel: (value: RoleLevel) => void;

  partners: OptionKey[];
  onSelectPartners: (values: OptionKey[]) => void;
}

const OPTIONS: Option[] = [
  {
    key: RoleLevel.All,
    value: 'All',
    title: 'All',
  },
  {
    key: RoleLevel.Elevate,
    value: 'Elevate',
    title: 'Elevate',
  },
  {
    key: RoleLevel.Global,
    value: 'Global',
    title: 'Global',
  },
  {
    key: RoleLevel.Partner,
    value: 'Partner',
    title: 'Partner',
  },
];

const StyledText = styled(Text)`
  &:hover {
    color: ${({ theme }) => theme.colors.danger}
  }
`;

const OFFSET = 20;

export const RolesLevelDropdown: React.FC<Props> = ({ level, onSetLevel, onSelectPartners, partners }) => {
  const [currentLevel, setCurrentLevel] = useState<RoleLevel>(level || RoleLevel.All);
  const { isElevateOrganization, observingMode } = useCurrentOrganization();
  const [currentPartners, setCurrentPartners] = useState<OptionKey[]>(partners);

  const [reservedPartners, setReserverPartners] = useState<OptionKey[]>(partners);
  const [searchString, setSearchString] = useState('');
  const { setValues: setDebounceSearchString, values: debouncedSearchString } = useDebounceState<string>();
  const {
    handleSync,
    options: partnerOptions,
    isLoading: isLoadingPartners,
    handleLoadMore,
  } = useRolePartnersQuery({
    searchString,
    page: 1,
    perPage: 10,
  });
  
 

  useEffect(() => {
    (async () => {
      if(reservedPartners.length) {
        await handleSync({
          partners: reservedPartners
        });
      }
      await handleSync();
    })();
   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchString]);
  useEffect(() => {
    setDebounceSearchString(searchString);
  }, [searchString, setDebounceSearchString]);

  const [expanded, setExpanded] = useState(false);
  

  const handleReset = useCallback(() => {
    setCurrentPartners([]);
    onSelectPartners([]);
    setSearchString('');
    onSetLevel(RoleLevel.All);
    setCurrentLevel(RoleLevel.All);
  }, [onSelectPartners, onSetLevel]);

  const currentTitle = useMemo(() => {
    if (level) {
      const formattedLevel = _.capitalize(level);
      if (partners.length) {
        const filteredPartners = partnerOptions
          .filter((option) => partners.includes(option.key))
          .map((option) => option.title);
        if (filteredPartners.length) {
          return `${formattedLevel} - ${filteredPartners.join(',')}`;
        }
      }
      return formattedLevel;
    }
    return 'All';
  }, [level, partnerOptions, partners]);

  const handleCancel = useCallback(() => {
    setExpanded(false);
    handleReset();
  }, [handleReset]);

  const handleApply = useCallback(() => {
    onSelectPartners(currentPartners);
    setReserverPartners(currentPartners);
    onSetLevel(currentLevel);
    setExpanded(false);
    handleSync();
  }, [currentLevel, currentPartners, handleSync, onSelectPartners, onSetLevel]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement, Event>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + OFFSET) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  const handleSetLevel = useCallback((value: RoleLevel) => {
    setCurrentLevel(value);
    if (value !== RoleLevel.Partner) {
      setCurrentPartners([]);
    }
  }, []);
  const handleSetPartner = useCallback(
    (value: OptionKey) => {
      if (currentPartners.includes(value)) {
        setCurrentPartners(currentPartners.filter((option) => option !== value));
      } else {
        setCurrentPartners([...currentPartners, value]);
        setCurrentLevel(RoleLevel.Partner);
      }
    },
    [currentPartners]
  );
  const isElevateWithoutObservingMode = useMemo(() => {
    return isElevateOrganization && !observingMode;
  }, [isElevateOrganization, observingMode]);
  const currentOptions = useMemo(() => {
    let options = OPTIONS;
    if (!isElevateWithoutObservingMode) {
      options = options.filter((option) => option.key !== RoleLevel.Elevate);
    }

    return options;
  }, [isElevateWithoutObservingMode]);
  return (
    <Box width="240px" data-testid="roles-level_dropdown">
      <Dropdown
        placeholder="Placeholder"
        isClickOutside={false}
        testId='role_level'
        prefix={isElevateWithoutObservingMode ? 'Level/Assigned to': 'Level'}
        expanded={expanded}
        title={(
          <Text title={currentTitle} color="textAccent" truncate>
            {currentTitle}
          </Text>
        )}
        onSetExpanded={setExpanded}
        contentStyles={{
          elevation: 'active',
          style: {
            right: '',
            minWidth: '340px',
          },
        }}
      >
        <Box direction="column" data-testid="roles-level-dropdown_content" >
          {isElevateWithoutObservingMode && (
            <Box pad="m" direction="column">
              <SearchInput value={searchString} defaultValue={searchString} placeholder="Search" onChange={setSearchString} />
            </Box>
          )}
          
          <Box
            ref={wrapperRef}
            pad={{ top: 'xs', left: 'm', right: 'm', bottom: 'm' }}
            border={{ side: 'horizontal', size: 'small', color: 'border1' }}
            style={{ overflow: 'auto' }}
            height={isElevateWithoutObservingMode?'290px': undefined}
            onScroll={handleScroll}
          >
            <div>
              <RawButton onClick={handleReset}>
                <StyledText size="small" color="textSecondary">
                  Reset
                </StyledText>
              </RawButton>
              {currentOptions.map((option) => (
                <RolesLevelRadio
                  checked={currentLevel === option.key}
                  onChangeValue={handleSetLevel}
                  role={option.key as RoleLevel}
                  key={option.key}
                  name={option.key as string}
                  label={option.title as string}
                >
                  {option.key === RoleLevel.Partner && isElevateWithoutObservingMode ? (
                    <>
                      <RolesLevelPartnerDropdown
                        partners={currentPartners}
                        onSelectPartner={handleSetPartner}
                        options={partnerOptions}
                      />
                      {isLoadingPartners && (
                        <Box>
                          <Preloader></Preloader>
                        </Box>
                      )}
                    </>
                  ) : null}
                </RolesLevelRadio>
              ))}
            </div>
          </Box>

          <Box pad="m" direction="row">
            <Box margin={{ right: 'spacing6' }}>
              <AppButton testId='role-level-dropdown_cancel' width="145px" buttonType="secondary" onClick={handleCancel}>
                Cancel
              </AppButton>
            </Box>
            <Box margin={{ left: 'spacing6' }}>
              <AppButton  testId='role-level-dropdown_apply' width="145px" onClick={handleApply}>
                Apply
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Dropdown>
    </Box>
  );
};
export default React.memo(RolesLevelDropdown);
